/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { ProjectTypeResponse } from '../models/project-type-response';
import { ProjectTypeRequest } from '../models/project-type-request';

/**
 * Ui Project Type Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiProjectTypeControllerService extends __BaseService {
  static readonly getAllProjectTypesUsingGETPath = '/cc-ui/v1/project-types';
  static readonly addProjectTypeUsingPOSTPath = '/cc-ui/v1/project-types';
  static readonly getProjectTypeByIdUsingGETPath =
    '/cc-ui/v1/project-types/{id}';
  static readonly updateProjectTypeUsingPUTPath =
    '/cc-ui/v1/project-types/{id}';
  static readonly deleteProjectTypeUsingDELETEPath =
    '/cc-ui/v1/project-types/{id}';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Get all project types
   *
   * - **Description:** Retrieve a list of all existing project types.
   * - **Restrictions:** None.
   * - **Permissions:** No specific permissions required.
   * - **Audit Logging:** Not applicable.
   * @return Success
   */
  getAllProjectTypesUsingGETResponse(): __Observable<
    __StrictHttpResponse<Array<ProjectTypeResponse>>
  > {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/project-types`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<ProjectTypeResponse>>;
      }),
    );
  }
  /**
   * Get all project types
   *
   * - **Description:** Retrieve a list of all existing project types.
   * - **Restrictions:** None.
   * - **Permissions:** No specific permissions required.
   * - **Audit Logging:** Not applicable.
   * @return Success
   */
  getAllProjectTypesUsingGET(): __Observable<Array<ProjectTypeResponse>> {
    return this.getAllProjectTypesUsingGETResponse().pipe(
      __map((_r) => _r.body as Array<ProjectTypeResponse>),
    );
  }

  /**
   * Add a new project type
   *
   * - **Description:** Creates a new project type from the provided request details.
   * - **Restrictions:** Only users with the appropriate permissions can add project types.
   * - **Permissions:** Requires `PROJECT_TYPE_WRITE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param projectTypeRequest projectTypeRequest
   * @return Project type created successfully
   */
  addProjectTypeUsingPOSTResponse(
    projectTypeRequest: ProjectTypeRequest,
  ): __Observable<__StrictHttpResponse<ProjectTypeResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = projectTypeRequest;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/project-types`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ProjectTypeResponse>;
      }),
    );
  }
  /**
   * Add a new project type
   *
   * - **Description:** Creates a new project type from the provided request details.
   * - **Restrictions:** Only users with the appropriate permissions can add project types.
   * - **Permissions:** Requires `PROJECT_TYPE_WRITE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param projectTypeRequest projectTypeRequest
   * @return Project type created successfully
   */
  addProjectTypeUsingPOST(
    projectTypeRequest: ProjectTypeRequest,
  ): __Observable<ProjectTypeResponse> {
    return this.addProjectTypeUsingPOSTResponse(projectTypeRequest).pipe(
      __map((_r) => _r.body as ProjectTypeResponse),
    );
  }

  /**
   * Get project type by ID
   *
   * - **Description:** Retrieve a specific project type by its ID.
   * - **Restrictions:** None.
   * - **Permissions:** No specific permissions required.
   * - **Audit Logging:** Not applicable.
   * @param id id
   * @return Success
   */
  getProjectTypeByIdUsingGETResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<ProjectTypeResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/project-types/${encodeURIComponent(String(id))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ProjectTypeResponse>;
      }),
    );
  }
  /**
   * Get project type by ID
   *
   * - **Description:** Retrieve a specific project type by its ID.
   * - **Restrictions:** None.
   * - **Permissions:** No specific permissions required.
   * - **Audit Logging:** Not applicable.
   * @param id id
   * @return Success
   */
  getProjectTypeByIdUsingGET(id: string): __Observable<ProjectTypeResponse> {
    return this.getProjectTypeByIdUsingGETResponse(id).pipe(
      __map((_r) => _r.body as ProjectTypeResponse),
    );
  }

  /**
   * Update an existing project type
   *
   * - **Description:** Updates details of a specified project type by its ID.
   * - **Restrictions:** Only users with the correct permissions can update project types.
   * - **Permissions:** Requires `PROJECT_TYPE_WRITE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param params The `UiProjectTypeControllerService.UpdateProjectTypeUsingPUTParams` containing the following parameters:
   *
   * - `projectTypeRequest`: projectTypeRequest
   *
   * - `id`: id
   *
   * @return Project type updated successfully
   */
  updateProjectTypeUsingPUTResponse(
    params: UiProjectTypeControllerService.UpdateProjectTypeUsingPUTParams,
  ): __Observable<__StrictHttpResponse<ProjectTypeResponse>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.projectTypeRequest;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/project-types/${encodeURIComponent(String(params.id))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ProjectTypeResponse>;
      }),
    );
  }
  /**
   * Update an existing project type
   *
   * - **Description:** Updates details of a specified project type by its ID.
   * - **Restrictions:** Only users with the correct permissions can update project types.
   * - **Permissions:** Requires `PROJECT_TYPE_WRITE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param params The `UiProjectTypeControllerService.UpdateProjectTypeUsingPUTParams` containing the following parameters:
   *
   * - `projectTypeRequest`: projectTypeRequest
   *
   * - `id`: id
   *
   * @return Project type updated successfully
   */
  updateProjectTypeUsingPUT(
    params: UiProjectTypeControllerService.UpdateProjectTypeUsingPUTParams,
  ): __Observable<ProjectTypeResponse> {
    return this.updateProjectTypeUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as ProjectTypeResponse),
    );
  }

  /**
   * Delete a project type
   *
   * - **Description:** Deletes an existing project type based on its ID.
   * - **Restrictions:** Only users with the correct RBAC permission can delete a project type.
   * - **Permissions:** Requires `PROJECT_TYPE_DELETE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param id id
   */
  deleteProjectTypeUsingDELETEResponse(
    id: string,
  ): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl +
        `/cc-ui/v1/project-types/${encodeURIComponent(String(id))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      }),
    );
  }
  /**
   * Delete a project type
   *
   * - **Description:** Deletes an existing project type based on its ID.
   * - **Restrictions:** Only users with the correct RBAC permission can delete a project type.
   * - **Permissions:** Requires `PROJECT_TYPE_DELETE` permission.
   * - **Audit Logging:** This operation is logged for audit purposes.
   * @param id id
   */
  deleteProjectTypeUsingDELETE(id: string): __Observable<null> {
    return this.deleteProjectTypeUsingDELETEResponse(id).pipe(
      __map((_r) => _r.body as null),
    );
  }
}

module UiProjectTypeControllerService {
  /**
   * Parameters for updateProjectTypeUsingPUT
   */
  export interface UpdateProjectTypeUsingPUTParams {
    /**
     * projectTypeRequest
     */
    projectTypeRequest: ProjectTypeRequest;

    /**
     * id
     */
    id: string;
  }
}

export { UiProjectTypeControllerService };
